import React, { useState, useEffect } from 'react';
import { stripeConnectApi, subscriptionApi } from '../../api';
import { SubscriptionPlan } from '../../api_types';
import LoadingSpinner from '../../shared/components/LoadingSpinner';

const PlanManagement: React.FC<{ businessId: string, facilityId: string }> = ({ businessId, facilityId }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);

    useEffect(() => {
        const fetchPlans = async () => {
            if (!businessId || !facilityId) {
                setError('No facility ID found');
                setLoading(false);
                return;
            }

            try {
                const response = await stripeConnectApi.getPlans(businessId, facilityId);
                setPlans(response.plans);
            } catch (error) {
                console.error('Error fetching plans:', error);
                setError('Failed to fetch plans');
            } finally {
                setLoading(false);
            }
        };

        fetchPlans();
    }, [businessId, facilityId]);

    const handleArchivePlan = async (planId: string) => {
        if (!window.confirm('Are you sure you want to archive this plan?')) {
            return;
        }

        try {
            setLoading(true);
            await subscriptionApi.archiveSubscriptionPlan(planId, facilityId, businessId);
            setPlans(plans.filter(p => p.id !== planId));
        } catch (error) {
            console.error('Error archiving plan:', error);
            setError('Failed to archive plan');
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount / 100);
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-md">
                <div className="p-4 bg-red-100 text-red-700 rounded">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4">Subscription Plans</h2>
            
            {plans.length === 0 ? (
                <p className="text-gray-600">No plans created yet.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Price
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Initiation Fee
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Interval
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Created
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {plans.map((plan) => (
                                <tr key={plan.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {plan.name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {formatPrice(plan.amount)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {formatPrice(plan.initiationFee || 0)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {plan.interval.charAt(0).toUpperCase() + plan.interval.slice(1)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {new Date(plan.createdAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <button
                                            className="text-red-600 hover:text-red-900"
                                            onClick={() => handleArchivePlan(plan.id)}
                                        >
                                            Archive
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default PlanManagement; 